import React from 'react';
import { PermissionDomains } from './types';
import { useHasPermission, useIsProfileOwner } from './hooks';

type ConditionalRenderProps = {
    children: React.ReactNode;
};

type RenderWithAccessRenderProps = {
    renderWithAccess: () => React.ReactNode;
};

type RenderCallbackRenderProps = {
    renderCallback: (hasAccess: boolean) => React.ReactNode;
};

type RenderProps = ConditionalRenderProps | RenderWithAccessRenderProps | RenderCallbackRenderProps;

type BaseAccessCheckProps = {
    hasAccess: boolean;
} & RenderProps;

const BaseAccessCheck: React.FC<BaseAccessCheckProps> = (props) => {
    if ('renderWithAccess' in props && props.hasAccess) {
        return props.renderWithAccess();
    } else if ('renderCallback' in props) {
        return props.renderCallback(props.hasAccess);
    } else if ('children' in props && props.hasAccess) {
        return <>{props.children}</>;
    } else {
        return null;
    }
};

export type PermissionDomainCheckProps = {
    domain: PermissionDomains;
    permission: 'view' | 'edit';
    medicalProfileId: string;
} & RenderProps;

export const PermissionDomainCheck: React.FC<PermissionDomainCheckProps> = (props) => {
    const hasAccess = useHasPermission(props.medicalProfileId, props.domain, props.permission);

    return <BaseAccessCheck {...props} hasAccess={hasAccess} />;
};

export type ProfileOwnerCheckProps = {
    medicalProfileId: string;
} & RenderProps;

export const ProfileOwnerCheck: React.FC<ProfileOwnerCheckProps> = (props) => {
    const hasAccess = useIsProfileOwner(props.medicalProfileId);

    return <BaseAccessCheck {...props} hasAccess={hasAccess} />;
};

export const CollaboratorCheck: React.FC<ProfileOwnerCheckProps> = (props) => {
    const hasAccess = useIsProfileOwner(props.medicalProfileId);

    return <BaseAccessCheck {...props} hasAccess={!hasAccess} />;
};
